'use client';

import { useAtomSet } from '@effect/atom-react';
import { addProductsToFavouriteListAtomFn } from '@vs-shop/engine/atom/favourites/addProductsToFavouriteListAtomFn';
import {
  invalidateFavouriteListProductsAtomFn,
  invalidateFavouriteListsAtomFn,
} from '@vs-shop/engine/atom/favourites/invalidateFavouriteLists';
import { Array, Effect, Equal, Exit, Option } from 'effect';
import { useEffect, useMemo, useState, useTransition } from 'react';

import { useAppRouter } from '../../../../shared/router/AppRouterContext';
import { captureCause } from '../../../../shared/utils/sentry';
import { deleteProductsFromFavouriteListAtomFn } from '../../../../effect/atom/favourites/deleteProductsFromFavouriteListAtomFn';
import type { ProductFavouriteListWithProductCodes } from '../../../../effect/schema/openapi/backend';

export type FavouriteMembershipSaveOptions = {
  readonly productCode: string;
  readonly listsData: readonly ProductFavouriteListWithProductCodes[];
  readonly isLoading: boolean;
  readonly isFavourite: boolean;
  readonly onSuccessNotification: () => void;
  readonly onFailureNotification: () => void;
  readonly onCloseAction: () => void;
};

export type FavouriteMembershipSaveState = {
  readonly checkedListIds: ReadonlySet<string>;
  readonly validationError: boolean;
  readonly isPending: boolean;
  readonly handleCheckedChange: (listId: string, checked: boolean) => void;
  readonly handleSave: () => void;
};

export const useFavouriteMembershipSave = ({
  productCode,
  listsData,
  isLoading,
  isFavourite,
  onSuccessNotification,
  onFailureNotification,
  onCloseAction,
}: FavouriteMembershipSaveOptions): FavouriteMembershipSaveState => {
  const router = useAppRouter();
  const [isPending, startTransition] = useTransition();
  const [checkedListIds, setCheckedListIds] = useState<Set<string>>(new Set());
  const [validationError, setValidationError] = useState(false);

  const initialCheckedIds = useMemo(() => {
    const matchingIds = Array.reduce(listsData, Array.empty<string>(), (ids, list) => {
      const productVariantCodes = Option.fromNullishOr(list.productVariantCodes).pipe(
        Option.getOrElse(() => [])
      );
      const listId = Option.fromNullishOr(list.id).pipe(Option.getOrElse(() => ''));

      return Array.contains(productVariantCodes, productCode) ? Array.append(ids, listId) : ids;
    });
    return new Set(matchingIds);
  }, [listsData, productCode]);

  const [hasInitialized, setHasInitialized] = useState(false);

  const addProductsToFavouriteListMutation = useAtomSet(addProductsToFavouriteListAtomFn, {
    mode: 'promiseExit',
  });
  const deleteProductsFromFavouriteListMutation = useAtomSet(
    deleteProductsFromFavouriteListAtomFn,
    {
      mode: 'promiseExit',
    }
  );
  const invalidateFavouriteLists = useAtomSet(invalidateFavouriteListsAtomFn);
  const invalidateFavouriteListProducts = useAtomSet(invalidateFavouriteListProductsAtomFn);

  useEffect(() => {
    if (!hasInitialized && !isLoading) {
      setHasInitialized(true);
      setCheckedListIds(initialCheckedIds);
    }
  }, [hasInitialized, isLoading, initialCheckedIds]);

  const handleCheckedChange = (listId: string, checked: boolean) => {
    setValidationError(false);
    setCheckedListIds((previous) => {
      const previousIds = Array.fromIterable(previous);
      const nextIds = checked
        ? Array.contains(previousIds, listId)
          ? previousIds
          : Array.append(previousIds, listId)
        : Array.filter(previousIds, (previousId) => !Equal.equals(previousId, listId));
      return new Set(nextIds);
    });
  };

  const handleSave = () => {
    const isUnchanged = Equal.equals(checkedListIds, initialCheckedIds);

    if (!isFavourite && isUnchanged) {
      setValidationError(true);
      return;
    }

    startTransition(async () => {
      const selectedIds = Array.fromIterable(checkedListIds);
      const baselineIds = Array.fromIterable(initialCheckedIds);
      const listsToAdd = Array.filter(
        selectedIds,
        (listId) => !Array.contains(baselineIds, listId)
      );
      const listsToRemove = Array.filter(
        baselineIds,
        (listId) => !Array.contains(selectedIds, listId)
      );

      if (Array.isReadonlyArrayEmpty(listsToAdd) && Array.isReadonlyArrayEmpty(listsToRemove)) {
        onCloseAction();
        return;
      }

      const program = Effect.gen(function* () {
        const additionOperations = Array.map(listsToAdd, (id) =>
          Effect.promise(() =>
            addProductsToFavouriteListMutation({
              uuid: id,
              productVariantCodes: [productCode],
            })
          ).pipe(
            Effect.map((exit) => {
              if (Exit.isFailure(exit)) {
                captureCause(exit.cause, {
                  component: 'ProductFavouritesModalContent',
                  action: 'Favourite list operation failed',
                  additionalData: { productCode },
                });
                return true;
              }
              return false;
            })
          )
        );

        const removalOperations = Array.map(listsToRemove, (id) =>
          Effect.promise(() =>
            deleteProductsFromFavouriteListMutation({
              uuid: id,
              productVariantCodes: [productCode],
            })
          ).pipe(
            Effect.map((exit) => {
              if (Exit.isFailure(exit)) {
                captureCause(exit.cause, {
                  component: 'ProductFavouritesModalContent',
                  action: 'Favourite list operation failed',
                  additionalData: { productCode },
                });
                return true;
              }
              return false;
            })
          )
        );
        const operations = Array.appendAll(additionOperations, removalOperations);

        if (Array.isReadonlyArrayEmpty(operations)) {
          return false;
        }

        const results = yield* Effect.all(operations);
        return Array.some(results, (hasFailure) => hasFailure);
      });

      const hasError = await Effect.runPromise(program);

      if (hasError) {
        onFailureNotification();
      } else {
        onSuccessNotification();
        invalidateFavouriteLists();
        invalidateFavouriteListProducts();
        router.refresh();
        onCloseAction();
      }
    });
  };

  return {
    checkedListIds,
    validationError,
    isPending,
    handleCheckedChange,
    handleSave,
  };
};
